import { CaretLeft, WarningCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Avatar, Badge } from '../components/ui';
import { useAuth } from '@/core/AuthContext';
import { useTrainerDetails } from '@/lib/useTrainerDetails';

export function TrainerContact() {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const trainerDetails = useTrainerDetails({
    trainerId: userData?.trainerId || userData?.trainerProfileId,
    aiTrainerId: userData?.aiTrainerId,
    enabled: Boolean(userData),
  });
  const trainerError = !trainerDetails.loading && !trainerDetails.name
    ? trainerDetails.error || 'Trainer not found'
    : null;

  const generateVCard = () => {
    if (!trainerDetails.name) return;

    const vCardFirstName = trainerDetails.name;
    const vCardLastName = '';
    const photoUrl = trainerDetails.photoURL || '';

    let vCard = `BEGIN:VCARD
VERSION:3.0
FN:${trainerDetails.name}
N:${vCardLastName};${vCardFirstName};;;
ORG:${trainerDetails.company}
TITLE:${trainerDetails.label}
EMAIL:${trainerDetails.email}`;

    if (photoUrl) {
      vCard += `\nPHOTO;VALUE=URI:${photoUrl}`;
    }

    if (trainerDetails.phone) {
      vCard += `\nTEL;TYPE=CELL:${trainerDetails.phone}`;
    }

    if (trainerDetails.persona) {
      vCard += `\nNOTE:${trainerDetails.persona}`;
    }

    vCard += `\nEND:VCARD`;

    const blob = new Blob([vCard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${trainerDetails.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 safe-top">
        <div className="mx-auto w-full max-w-md">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client')}
            className="mb-2"
          >
            <CaretLeft size={16} weight="bold" />
            Back to Dashboard
          </Button>
          <Badge variant="info" size="sm" className="mb-1">
            Support
          </Badge>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Trainer Contact
          </h1>
        </div>
      </div>
      <main className="p-6 flex flex-1 items-center justify-center">
        {trainerDetails.loading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-slate-400">Loading trainer info...</p>
          </div>
        ) : trainerError ? (
          <Card variant="elevated" padding="lg" className="max-w-md">
            <CardContent>
              <div className="text-center space-y-4">
                <div className="flex justify-center text-amber-500">
                  <WarningCircle size={40} weight="fill" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {trainerError}
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Please contact support if you need assistance.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <Card variant="elevated" padding="lg" className="text-center">
              <CardContent>
                <div className="space-y-6">
                  {/* Trainer Photo */}
                  <div className="flex justify-center">
                    <motion.div
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    >
                      <Avatar
                        src={trainerDetails.photoURL}
                        alt={trainerDetails.name}
                        fallback={trainerDetails.name}
                        size="xl"
                        className="w-32 h-32 text-3xl shadow-lg"
                      />
                    </motion.div>
                  </div>

                    {/* Trainer Name & Title */}
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {trainerDetails.name}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {trainerDetails.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                      {trainerDetails.company}
                    </p>
                  </div>

                  {/* Persona */}
                  {trainerDetails.persona && (
                    <div className="bg-primary-50 dark:bg-primary-500/10 rounded-lg p-3">
                      <p className="text-sm text-primary-900 dark:text-primary-300 italic">
                        "{trainerDetails.persona}"
                      </p>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-white/10">
                    {/* Email */}
                    <div className="flex items-center justify-center gap-3">
                      <div className="p-2 rounded-lg bg-accent-50 dark:bg-accent-500/10">
                        <svg
                          className="w-5 h-5 text-accent-600 dark:text-accent-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-gray-500 dark:text-slate-500">Email</p>
                        <a
                          href={`mailto:${trainerDetails.email}`}
                          className="text-base font-semibold text-gray-900 dark:text-white hover:text-accent-600 dark:hover:text-accent-400 transition"
                        >
                          {trainerDetails.email}
                        </a>
                      </div>
                    </div>
                    {trainerDetails.phone && (
                      <div className="flex items-center justify-center gap-3">
                        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-500/10">
                          <svg
                            className="w-5 h-5 text-primary-600 dark:text-primary-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                        </div>
                        <div className="text-left">
                          <p className="text-xs text-gray-500 dark:text-slate-500">Phone</p>
                          <a
                            href={`tel:${trainerDetails.phone}`}
                            className="text-base font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition"
                          >
                            {trainerDetails.phone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Download vCard Button */}
                  <div className="pt-4">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={generateVCard}
                      className="w-full"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Download Contact
                    </Button>
                    <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                      Save to your phone's contacts
                    </p>
                  </div>

                  {/* Info Note */}
                  <div className="pt-4 border-t border-gray-200 dark:border-white/10">
                    <p className="text-xs text-gray-600 dark:text-slate-400">
                      Your dedicated trainer is here to help you reach your fitness goals.
                      Don't hesitate to reach out!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}
